import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getBranchesAction, deleteBranchAction } from "../../../store/actions/BranchAction"
import swal from "sweetalert";
import Loader from "../Common/Loader";

const Branch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branches.branches);
  const numRows = useSelector((state) => state.branches.num_rows || 0); // Add this to get total rows

  const [searchQueries, setSearchQueries] = useState({
    branch_name: "",
    branch_status: "",
  });

  const [sortField, setSortField] = useState("branch_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    const fetchBranches = async () => {
      setLoading(true);
      setError("");
      try {
        if (location.state?.returnTo) {
          const { currentPage, searchQueries, sortField, sortOrder } =
            location.state.returnTo;
          setCurrentPage(currentPage || 0);
          setSearchQueries(searchQueries || {});
          setSortField(sortField || "branch_name");
          setSortOrder(sortOrder || "asc");
        }
        await fetchBranchesFromServer();
      } catch (err) {
        console.error("Failed to fetch branches:", err);
        setError("Failed to load branches. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, [dispatch]);

  const fetchBranchesFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder,
  ) => {
    try {
      await dispatch(
        getBranchesAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (err) {
      console.error("Failed to fetch branches:", err);
      setError("Failed to load branches.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBranchesFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      page,
      sortField,
      sortOrder
    );
  };

  // Handle search - Modified to clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries and set only the current field
    const clearedQueries = {
      branch_name: "",
      branch_status: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Track active search
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);

    fetchBranchesFromServer(field, keyword, true, 0, sortField, sortOrder);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0); // reset to first page on new sort
    fetchBranchesFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order);
  };

  const handleEdit = (branch) => {
    navigate(`/branch/update-branch/${branch.branch_id}`, {
      state: {
        branch,
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    });
  };

  const handleDelete = (branch) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this branch!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteBranchAction(branch));
          // Refresh the current page after deletion
          await fetchBranchesFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete branch");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Server already returns paginated data
  const paginatedData = branches;
  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/add-branch"
            state={{
              returnTo: {
                currentPage,
                searchQueries,
                sortField,
                sortOrder,
              },
            }}
            className="btn btn-primary btn-rounded"
          >
            + Add Branch
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {/* Responsive Table Wrapper */}
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("branch_name")} style={{ cursor: "pointer" }}>
                            Branch Name {sortField === "branch_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.branch_name}
                              onChange={(e) => handleSearch(e, "branch_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Branch"
                            />
                          </th>
                          <th onClick={() => handleSort("branch_status")} style={{ cursor: "pointer" }}>
                            Status {sortField === "branch_status" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.branch_status}
                              onChange={(e) => handleSearch(e, "branch_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((branch) => (
                          <tr key={branch.branch_id}>
                            <td>{branch.branch_name}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${
                                  branch.branch_status.toLowerCase() === "active"
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}
                              >
                                {branch.branch_status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex">
                                <span
                                  className="edit me-4"
                                  onClick={() => handleEdit(branch)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Branch"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(branch)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Branch"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No branches found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {branches?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        // onClick={() =>
                        //   setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                        // }
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Branch;
